import { useForm } from "@inertiajs/react";
import { IndustrySelect } from "@/components/IndustrySelect";
import IndustrySelector from "@/components/IndustrySelector";
import ListingStatusSelect from "@/components/ListingStatusSelect";
import LogoUpload from "@/components/LogoUpload";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { GicsTreeNode } from "@/lib/industry";
import type {
  AccountListingStatus,
  BizminerIndustry,
} from "@/types/serializers";

interface CreateAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: number;
  gicsTree: GicsTreeNode[];
  industries: BizminerIndustry[];
}

export function CreateAccountDialog({
  open,
  onOpenChange,
  organizationId,
  gicsTree,
  industries,
}: CreateAccountDialogProps) {
  const { data, setData, post, processing, reset, transform } = useForm({
    account: {
      name: "",
      logo: null as File | null,
      industry_id: null as number | null,
      gics_sub_industry_id: null as number | null,
      listing_status: "" as AccountListingStatus | "",
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    transform((formData) => {
      const submitData: Record<string, unknown> = {
        account: {
          name: formData.account.name,
          industry_id: formData.account.industry_id,
          gics_sub_industry_id: formData.account.gics_sub_industry_id,
          listing_status: formData.account.listing_status,
        },
      };

      if (formData.account.logo) {
        (submitData.account as Record<string, unknown>).logo =
          formData.account.logo;
      }

      return submitData;
    });

    post(`/admin/organizations/${organizationId}/accounts`, {
      forceFormData: !!data.account.logo,
      onSuccess: () => {
        onOpenChange(false);
        reset();
      },
    });
  };

  const handleClose = () => {
    onOpenChange(false);
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[525px] text-foreground">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create New Account</DialogTitle>
            <DialogDescription>
              Add a new account to this organization. You can optionally add a
              logo.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="account-name">
                Account Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="account-name"
                type="text"
                value={data.account.name}
                onChange={(e) => setData("account.name", e.target.value)}
                placeholder="Enter account name"
                required
                autoFocus
              />
            </div>

            <div className="grid gap-2">
              <Label>Industry</Label>
              <IndustrySelect
                value={data.account.industry_id}
                onChange={(value) => setData("account.industry_id", value)}
                industries={industries}
              />
              <p className="text-xs text-muted-foreground">
                Determines which companies are offered as peers for comparison
              </p>
            </div>

            <div className="grid gap-2">
              <Label>GICS Classification</Label>
              <IndustrySelector
                value={data.account.gics_sub_industry_id}
                onChange={(value) =>
                  setData("account.gics_sub_industry_id", value)
                }
                treeData={gicsTree}
              />
              <p className="text-xs text-muted-foreground">
                Reference classification. Peer comparison uses the Industry
                field above.
              </p>
            </div>

            <ListingStatusSelect
              value={data.account.listing_status}
              onChange={(value) => setData("account.listing_status", value)}
            />

            <LogoUpload
              label="Logo (Optional)"
              onLogoChange={(file) => setData("account.logo", file)}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                processing || !data.account.name || !data.account.listing_status
              }
            >
              {processing ? "Creating..." : "Create Account"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
